import { createReducer } from '@reduxjs/toolkit';
import { records as appStorageRecords } from '@saucelabs/sl-module-app-storage';

import { Preset } from './records';
import actions from './actions';
import operations from './operations';
import utils from './utils';

const { fetchPresets, fetchAppStorageFiles } = actions;
const { getPresetsWithMetadata } = operations;

interface PresetsState {
  readonly apps: appStorageRecords.AppStorageFile[];
  readonly elements: Preset[];
  readonly isInitial: boolean;
  readonly isLoading: boolean;
  readonly hasError: boolean;
}

const initialState: PresetsState = {
  apps: [],
  elements: [],
  hasError: false,
  isInitial: true,
  isLoading: false,
};

/**
 * Filter out "app test" presets where no detailed app information is available
 * Since we don't want to show any preset where we don't have all information
 * @param {PresetsState} PresetState
 * @returns {Preset[]} List of filtered presets
 */
const filterUnknowAppsFromPresets = ({ apps, elements }: PresetsState): Preset[] => {
  const appIds = apps.map((app) => app.id);
  return elements.filter((preset) => (
    !(preset.isAppTest() && !appIds.includes(preset.getAppId()))
  ));
};

const presetsReducer = createReducer(initialState, (builder) => {
  // fetchPresets
  builder.addCase(fetchPresets.fulfilled, (draft, { payload }) => {
    draft.elements = payload.items
      .map((serverPreset) => new Preset(serverPreset))
      .filter((preset) => utils.isValidPresetsSchema(preset));
    draft.hasError = false;
  });
  builder.addCase(fetchPresets.rejected, (draft) => {
    draft.elements = initialState.elements;
    draft.hasError = true;
  });

  // fetchAppStorageFiles
  builder.addCase(fetchAppStorageFiles.fulfilled, (draft, { payload }) => {
    draft.apps = payload.items;
  });

  // getPresetsWithMetadata
  builder.addCase(getPresetsWithMetadata.pending, (draft) => {
    draft.hasError = false;
    draft.isLoading = true;
  });
  builder.addCase(getPresetsWithMetadata.fulfilled, (draft) => {
    // everything seems to have worked but no guarantee all infos are available - let's clean up
    draft.elements = filterUnknowAppsFromPresets(draft);
    draft.isLoading = false;
    draft.isInitial = false;
  });
  builder.addCase(getPresetsWithMetadata.rejected, (draft) => {
    // something went wrong - let's clean up
    draft.elements = filterUnknowAppsFromPresets(draft);
    draft.isLoading = false;
    draft.isInitial = false;
  });
});

export default presetsReducer;
export {
  initialState,
  PresetsState,
};
